import {
    useCallback,
    Fragment,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import type {
    CompanyDetailPayload
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Seperator,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    NativeStackNavigationProp,
    NativeStackScreenProps
} from "@react-navigation/native-stack";
import {
    ArrowLeftIcon,
    PenIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

type Props = NativeStackScreenProps<RootStackParamList, "CompanyDetail">;

const CompanyDetail = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const route = useRoute<Props["route"]>();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const companyID = route.params?.companyID;

    const [
        companyData,
        setCompanyData
    ] = useState<CompanyDetailPayload | null>(null);

    const [
        sectorsData,
        setSectorsData
    ] = useState<{ _id: string; value: string }[]>([]);

    const [
        positionsData,
        setPositionsData
    ] = useState<{ _id: string; value: string }[]>([]);

    const [
        citiesData,
        setCitiesData
    ] = useState<{ _id: string; value: string }[]>([]);

    const [
        districtsData,
        setDistrictsData
    ] = useState<{ _id: string; value: string }[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const fetchCompanyDetail = useCallback(async () => {
        if(!companyID) {
            return;
        }

        try {
            setLoading(true);
            const [
                companyResult,
                sectorsResult,
                positionsResult,
                citiesResult
            ] = await Promise.all([
                RESTServiceContext.action("getCompanyForAdmin", {
                    companyID: companyID
                }),
                RESTServiceContext.action("getSectors", null),
                RESTServiceContext.action("getPositions", null),
                RESTServiceContext.action("getCities", null)
            ]);

            let fetchedCompanyData: CompanyDetailPayload | null = null;

            if(companyResult.payload) {
                fetchedCompanyData = companyResult.payload as CompanyDetailPayload;
                setCompanyData(fetchedCompanyData);
            }
            if(sectorsResult.payload) {
                setSectorsData(sectorsResult.payload as { _id: string; value: string }[]);
            }
            if(positionsResult.payload) {
                setPositionsData(positionsResult.payload as { _id: string; value: string }[]);
            }
            if(citiesResult.payload) {
                setCitiesData(citiesResult.payload as { _id: string; value: string }[]);
            }

            if(fetchedCompanyData?.cityID) {
                const distResult = await RESTServiceContext.action("getDistricts", {
                    cityID: fetchedCompanyData.cityID
                });
                if(distResult.payload) {
                    setDistrictsData(distResult.payload as { _id: string; value: string }[]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        companyID
    ]);

    useFocusEffect(
        useCallback(() => {
            fetchCompanyDetail();
        }, [
            fetchCompanyDetail
        ])
    );

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: isMobile ? spaces.spacingLg : spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: spaces.spacingSm
                }}
            >
                <Button
                    onPress={() => navigation.navigate("CompaniesList")}
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                >
                    {localize("companies-detail-title")}
                </Text>
            </View>
            <View
                style={{
                    width: isMobile ? "100%" : undefined,
                    flexDirection: "row",
                    gap: spaces.spacingSm
                }}
            >
                <Button
                    onPress={() => navigation.navigate("CompanyEdit", {
                        companyID: companyID || ""
                    })}
                    spreadBehaviour={isMobile ? "stretch" : "free"}
                    title={localize("companies-edit-button")}
                    icon={({
                        color,
                        size
                    }) => <PenIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    variant="filled"
                />
            </View>
        </View>;
    };

    if(loading) {
        return <PageContainer>
            <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    if(!companyData) {
        return <PageContainer>
            <View
                style={{
                    padding: spaces.spacingXl
                }}
            >
                <Text>{localize("companies-detail-not-found")}</Text>
            </View>
        </PageContainer>;
    }

    const initials = companyData.title.slice(0, 2).toUpperCase();

    const renderUserCard = (user: { profilePhotoPath?: string, positionID?: string, firstName?: string, lastName?: string, mail?: string, _id?: string }, label: string, keyVal: string) => {
        const userInitials = ((user.firstName || "")[0] || "") + ((user.lastName || "")[0] || "");
        const positionName = user.positionID ? positionsData.find(p => p._id === user.positionID)?.value || user.positionID : "-";

        return <TouchableOpacity
            style={[
                stylesheet.userCardContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    flexBasis: isMobile ? "100%" : 320,
                    borderWidth: borders.seperator,
                    padding: spaces.spacingMd,
                    borderRadius: radiuses.md,
                    gap: spaces.spacingMd,
                    flexShrink: 1,
                    flexGrow: 1
                }
            ]}
            onPress={() => {
                if(user._id) {
                    navigation.navigate("MemberDetail", {
                        userID: user._id
                    });
                }
            }}
            activeOpacity={0.7}
            key={keyVal}
        >
            <View
                style={[
                    stylesheet.userCardAvatar,
                    {
                        backgroundColor: colors.project.brandSurface,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.full
                    }
                ]}
            >
                {
                    user.profilePhotoPath ?
                        <Image
                            style={stylesheet.userCardAvatarImage}
                            source={{
                                uri: user.profilePhotoPath
                            }}
                            resizeMode="cover"
                        />
                    :
                        <Text
                            style={{
                                color: colors.project.gold
                            }}
                            variant="titleMediumSize"
                        >
                            {userInitials.toUpperCase()}
                        </Text>
                }
            </View>
            <View
                style={[
                    stylesheet.userCardInfo,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={{
                        color: colors.content.text.mid
                    }}
                    variant="bodySmallSize"
                >
                    {label}
                </Text>
                <Text
                    variant="titleMediumSize"
                >
                    {user.firstName} {user.lastName}
                </Text>
                {
                    (() => {
                        let posName = "-";
                        if (positionName) {
                            if (typeof positionName === "object") {
                                const posRecord = positionName as Record<string, string>;
                                posName = posRecord.tr || posRecord.en || "-";
                            } else {
                                posName = positionName as string;
                            }
                        }

                        if (posName !== "-") {
                            return <Text
                                style={{
                                    color: colors.content.text.mid
                                }}
                                variant="bodySmallSize"
                            >
                                {posName}
                            </Text>;
                        }

                        return null;
                    })()
                }
                {
                    user.mail ?
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {user.mail}
                        </Text>
                    :
                        null
                }
            </View>
        </TouchableOpacity>;
    };

    return <PageContainer
        scrollViewStyle={[
            stylesheet.container,
            {
                padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                gap: isMobile ? spaces.spacingMd : spaces.spacingXl
            }
        ]}
        isScrollable={true}
    >
        {renderHeader()}
        <View
            style={[
                stylesheet.profileCard,
                {
                    padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg
                }
            ]}
        >
            <View
                style={[
                    stylesheet.profileHeader,
                    {
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.avatarContainer,
                        {
                            backgroundColor: colors.project.brandSurface,
                            borderColor: colors.project.brandBorder,
                            borderWidth: borders.seperator,
                            height: isMobile ? 80 : 120,
                            borderRadius: radiuses.md,
                            width: isMobile ? 80 : 120
                        }
                    ]}
                >
                    {
                        companyData.companyPhotoPath ?
                            <Image
                                source={{
                                    uri: companyData.companyPhotoPath
                                }}
                                style={stylesheet.avatarImage}
                                resizeMode="cover"
                            />
                        :
                            <Text
                                variant={isMobile ? "titleLargeSize" : "displaySmallSize"}
                                style={{
                                    color: colors.project.gold
                                }}
                            >
                                {initials}
                            </Text>
                    }
                </View>
                <View
                    style={[
                        stylesheet.infoContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <View
                        style={{
                            alignItems: "center",
                            flexDirection: "row",
                            gap: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="displaySmallSize"
                        >
                            {companyData.title}
                        </Text>
                    </View>
                    <Text
                        style={{
                            color: colors.content.text.mid
                        }}
                        variant="titleMediumSize"
                    >
                        {companyData.mail || "-"}
                    </Text>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("companies-detail-contact-info")}
                </Text>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-phone")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.phoneNumber || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-website")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.website || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-foundation-year")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.foundationYear || "-"}
                        </Text>
                    </View>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("companies-detail-tax-info")}
                </Text>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-tax-office")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.taxOffice || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-tax-number")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.taxNumber || "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-is-exports")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.isExports ? localize("yes") : localize("no")}
                        </Text>
                    </View>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("companies-detail-other-info") || "Diğer Bilgiler"}
                </Text>
                <View
                    style={[
                        stylesheet.detailRow,
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-sectors") || "Sektörler"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.sectorIDs && companyData.sectorIDs.length > 0
                                ? companyData.sectorIDs.map(id => sectorsData.find(s => s._id === id)?.value || id).join(", ")
                                : "-"}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.detailCol}
                    >
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("companies-detail-products") || "Alınan/Satılan Ürünler"}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                        >
                            {companyData.productsBoughtSold && companyData.productsBoughtSold.length > 0
                                ? companyData.productsBoughtSold.join(", ")
                                : "-"}
                        </Text>
                    </View>
                </View>
            </View>
            <Seperator
                color="subtle"
            />
            <View
                style={[
                    stylesheet.detailSection,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("companies-detail-address-info")}
                </Text>
                <View
                    style={{
                        backgroundColor: colors.project.brandSurface,
                        padding: spaces.spacingMd,
                        borderRadius: radiuses.md
                    }}
                >
                    <Text
                        variant="bodyMediumSize"
                    >
                        {companyData.address || "-"}
                    </Text>
                    <Text
                        style={{
                            color: colors.content.text.mid,
                            marginTop: spaces.spacingXs
                        }}
                        variant="bodySmallSize"
                    >
                        {companyData.neighborhood || ""} {companyData.districtID ? `/ ${districtsData.find(d => d._id === companyData.districtID)?.value || companyData.districtID}` : ""} {companyData.cityID ? `/ ${citiesData.find(c => c._id === companyData.cityID)?.value || companyData.cityID}` : ""}
                    </Text>
                </View>
            </View>
            {
                companyData.ownerDetails ?
                    <Fragment>
                        <Seperator
                            color="subtle"
                        />
                        <View
                            style={[
                                stylesheet.detailSection,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                variant="titleMediumSize"
                            >
                                {localize("companies-detail-owner") || "Firma Sahibi"}
                            </Text>
                            <View
                                style={[
                                    stylesheet.detailRow,
                                    {
                                        gap: spaces.spacingMd,
                                        flexDirection: "row",
                                        flexWrap: "wrap"
                                    }
                                ]}
                            >
                                {renderUserCard(companyData.ownerDetails, localize("companies-detail-owner") || "Firma Sahibi", "owner")}
                            </View>
                        </View>
                    </Fragment>
                :
                    null
            }
            {
                (companyData.employees && companyData.employees.length > 0) ?
                    <Fragment>
                        <Seperator
                            color="subtle"
                        />
                        <View
                            style={[
                                stylesheet.detailSection,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                variant="titleMediumSize"
                            >
                                {localize("companies-detail-employees") || "Çalışanlar"}
                            </Text>
                            <View
                                style={[
                                    stylesheet.detailRow,
                                    {
                                        gap: spaces.spacingMd,
                                        flexDirection: "row",
                                        flexWrap: "wrap"
                                    }
                                ]}
                            >
                                {companyData.employees.map((emp, index) => renderUserCard(emp, localize("companies-detail-employees") || "Çalışan", `emp-${index}`))}
                            </View>
                        </View>
                    </Fragment>
                :
                    null
            }
            {
                companyData.aboutUs ?
                    <Fragment>
                        <Seperator
                            color="subtle"
                        />
                        <View
                            style={[
                                stylesheet.detailSection,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                variant="titleMediumSize"
                            >
                                {localize("companies-detail-about-us")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                            >
                                {companyData.aboutUs}
                            </Text>
                        </View>
                    </Fragment>
                :
                    null
            }
        </View>
    </PageContainer>;
};

export default CompanyDetail;
